import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { RouteLocationNormalized } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'

// Lazy loading for better performance
const Home = () => import('@/views/Home.vue')
const Login = () => import('@/views/Login.vue')
const Settings = () => import('@/views/Settings.vue')
const AuthCallback = () => import('@/views/AuthCallback.vue')

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: true }
  },
  {
    path: '/c/:conversationId',
    name: 'Conversation',
    component: Home,
    meta: { requiresAuth: true },
    props: true
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: AuthCallback,
    meta: { requiresAuth: false }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { requiresAuth: true }
  },
  // Redirect /chat to home since we only have one main interface
  {
    path: '/chat',
    redirect: '/'
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard for authentication
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next) => {
  const authStore = useAuthStore()
  
  console.debug('Router navigation guard:', {
    to: to.path,
    from: from.path,
    requiresAuth: to.meta.requiresAuth,
    isInitialized: authStore.isInitialized,
    isAuthenticated: authStore.isAuthenticated
  });
  
  // Always initialize auth store first
  try {
    if (!authStore.isInitialized) {
      console.debug('Initializing auth store from router guard');
      await authStore.initialize()
    }
  } catch (error) {
    console.error('Failed to initialize auth store:', error)
  }
  
  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      console.log('User not authenticated, redirecting to login', {
        route: to.path,
        hasUser: !!authStore.user,
        hasToken: !!authStore.accessToken
      });
      next({ name: 'Login', query: { redirect: to.fullPath } })
      return
    }
    
    // Check if user has valid joblogic.com email
    if (!authStore.isValidJoblogicUser) {
      console.log('Invalid joblogic user, redirecting to login with error', {
        userEmail: authStore.user?.email
      });
      next({ name: 'Login', query: { error: 'invalid_domain' } })
      return
    }
  }
  
  // If going to login but already authenticated, redirect to home  
  if (to.name === 'Login' && authStore.isAuthenticated && authStore.isValidJoblogicUser) {
    console.log('User already authenticated, redirecting to home');
    next({ name: 'Home' })
    return
  }
  
  console.debug('Router navigation allowed');
  next()
})

export default router
